import type { ReactNode } from "react";
import { useMemo } from "react";
import { Checkbox, Grid } from "@mui/material";
import {
  CommuneAssocieeDelegueeLink,
  CommuneLink,
  MetierBox,
  PerimetreLink,
  PosteBox,
  TerritoireLink,
} from "components/Business";
import { LoadingTableCell } from "./LoadingTableCell";
import ContactActionsCell from "./ContactActionsCell";
import { ListDensity, useContactMetierActions, useSearch, useVirtualizedList } from "providers";
import type { ListRowProps } from "react-virtualized";
import { CellMeasurer } from "react-virtualized";
import { grey } from "@mui/material/colors";
import { contactsMetiersUtil } from "utils";

const cellSx = { paddingY: 0.5, paddingX: 1 };

export function ContactMetiersTableRow({ index, style, parent }: ListRowProps): ReactNode {
  const { filteredContactsMetiers, isPerimetresLoading } = useSearch();
  const { selectedContactMetiers, toggleUniqueSelection, density } = useContactMetierActions();
  const { cache } = useVirtualizedList();

  const contactMetier = useMemo(
    () => filteredContactsMetiers[index],
    [filteredContactsMetiers, index]
  );

  const isSelected = useMemo(
    () => selectedContactMetiers.some((cm) => cm.id === contactMetier.id),
    [contactMetier.id, selectedContactMetiers]
  );

  return (
    <CellMeasurer cache={cache} parent={parent} columnIndex={0} rowIndex={index}>
      <Grid
        container
        style={style}
        size={12}
        sx={{
          width: "100%",
          borderBottom: `1px solid ${grey[300]}`,
          paddingLeft: 0.5,
          alignItems: "center",
        }}
        spacing={1}>
        <Grid size="auto">
          <Checkbox
            color="primary"
            checked={isSelected}
            onChange={() => {
              toggleUniqueSelection(contactMetier);
            }}
          />
        </Grid>
        {isPerimetresLoading ? (
          [1, 2].map((_key) => <LoadingTableCell key={_key} />)
        ) : (
          <>
            <Grid container spacing={0.5} direction="column" size={1.5} sx={cellSx}>
              {contactMetier.territoires?.map((t) => (
                <Grid key={t.id}>
                  <TerritoireLink territoire={t} />
                </Grid>
              ))}
            </Grid>
            <Grid
              container
              spacing={0.5}
              direction="column"
              size={density === ListDensity.DENSE ? { xs: 2, sm: 1.5 } : 3}
              sx={cellSx}>
              {contactMetier.perimetres?.map((p) => (
                <Grid key={p.id}>
                  <PerimetreLink perimetre={p} dense={density === ListDensity.DENSE} />
                </Grid>
              ))}
            </Grid>
          </>
        )}
        <Grid size={1.5} sx={cellSx}>
          {contactsMetiersUtil.isCommune(contactMetier) && (
            <CommuneLink commune={contactMetier.commune} />
          )}
          {contactsMetiersUtil.isCommuneAssocieeDeleguee(contactMetier) && (
            <CommuneAssocieeDelegueeLink communeAssocieeDeleguee={contactMetier.cad} />
          )}
        </Grid>
        <Grid size={density === ListDensity.DENSE ? 3 : 2.5} sx={cellSx}>
          <MetierBox metier={contactMetier.metier} noWrap={density === ListDensity.DENSE} />
        </Grid>
        <Grid size="grow" sx={cellSx}>
          <PosteBox poste={contactMetier.poste} noWrap={density === ListDensity.DENSE} />
        </Grid>
        <ContactActionsCell contactMetier={contactMetier} dense={density === ListDensity.DENSE} />
      </Grid>
    </CellMeasurer>
  );
}
